import React from "react";
import {
    ResponsiveContainer,
    ComposedChart,
    Line,
    Bar,
    YAxis,
    XAxis,
    Tooltip as RTooltip,
    ReferenceLine,
} from "recharts";
import { priceDecimals } from "@/lib/utils";

const fmtTime = (ms) => {
    const d = new Date(ms);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;
};

const fmtVol = (v) => {
    const n = Number(v) || 0;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    if (n >= 1) return n.toFixed(2);
    return n.toFixed(4);
};

/**
 * Live price chart with a traded-volume overlay.
 *  - Volume: bars on the LEFT axis (subtle, behind the price line).
 *  - Price:  line on the RIGHT axis, adaptive domain per pair.
 * `symbol` lets callers title it per pair (defaults to BTCUSDT).
 * `label` overrides the whole title (pages rendering two charts side by
 * side passed it already — it was silently dropped, so both charts showed
 * the same "<symbol> · Live Price" header).
 */
export const PriceChart = ({ data = [], buys = [], sells = [], symbol = "BTCUSDT", label = null }) => {
    const rawSeries = data
        .map((d) => ({
            ts: d.ts,
            price: Number(d.price),
            volume: Number(d.volume) || 0,
        }))
        .filter((d) => Number.isFinite(d.price) && d.price > 0);
    // Polling can return the same millisecond twice. Recharts derives tick
    // keys from value/coordinates, so duplicate timestamps produce duplicate
    // React keys even though the line itself looks correct.
    const series = Array.from(
        new Map(rawSeries.map((point) => [point.ts, point])).values(),
    ).sort((a, b) => Number(a.ts) - Number(b.ts));
    const hasVolume = series.some((d) => d.volume > 0);

    // Adaptive Y domain: pad by a FRACTION of the price range/value instead of
    // the old fixed ±$50 (fine for BTC ~100k, absurd for a $0.5 token where it
    // pinned the axis near 0 and flattened the line). Grid levels near the
    // price range are folded in so buy/sell reference lines stay visible.
    let priceDomain = ["auto", "auto"];
    let decimals = 2;
    if (series.length > 0) {
        let lo = Infinity, hi = -Infinity;
        for (const d of series) { if (d.price < lo) lo = d.price; if (d.price > hi) hi = d.price; }
        const span = hi - lo;
        const levelWindow = span > 0 ? span * 2 : hi * 0.02;
        for (const o of [...buys.slice(0, 10), ...sells.slice(0, 30)]) {
            const p = Number(o?.price);
            if (Number.isFinite(p) && p > 0 && Math.abs(p - (hi + lo) / 2) <= levelWindow) {
                if (p < lo) lo = p;
                if (p > hi) hi = p;
            }
        }
        const pad = Math.max((hi - lo) * 0.12, hi * 0.0004);
        priceDomain = [lo - pad, hi + pad];
        decimals = priceDecimals(hi);
    }

    return (
        <div className="panel p-4" data-testid="price-chart-container">
            <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#A7B4C6]">
                    {label || `${symbol} · Live Price`}
                </div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#6F7D92]">
                    {series.length} ticks{hasVolume ? " · vol" : ""}
                </div>
            </div>
            <div className="w-full min-w-0" style={{ height: 220 }}>
                {series.length < 2 ? (
                    <div className="h-full flex items-center justify-center text-[11px] text-[#3A4759]">
                        Collecting price history…
                    </div>
                ) : (
                <ResponsiveContainer width="100%" height={220} minWidth={1} minHeight={1} debounce={50}>
                    <ComposedChart data={series} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                        <XAxis
                            dataKey="ts"
                            tick={{ fill: "#6F7D92", fontSize: 10, fontFamily: "JetBrains Mono" }}
                            tickFormatter={fmtTime}
                            stroke="rgba(167,180,198,0.15)"
                            minTickGap={40}
                        />
                        {/* LEFT axis: volume */}
                        <YAxis
                            yAxisId="vol"
                            orientation="left"
                            domain={[0, (dataMax) => (dataMax > 0 ? dataMax * 4 : 1)]}
                            tick={{ fill: "#4A5A6E", fontSize: 9, fontFamily: "JetBrains Mono" }}
                            tickFormatter={fmtVol}
                            stroke="rgba(167,180,198,0.10)"
                            width={44}
                            allowDecimals={false}
                        />
                        {/* RIGHT axis: price (adaptive per pair) */}
                        <YAxis
                            yAxisId="price"
                            orientation="right"
                            domain={priceDomain}
                            tick={{ fill: "#6F7D92", fontSize: 10, fontFamily: "JetBrains Mono" }}
                            tickFormatter={(v) => Number(v).toFixed(decimals)}
                            stroke="rgba(167,180,198,0.15)"
                            width={72}
                            allowDataOverflow
                        />
                        <RTooltip
                            contentStyle={{
                                background: "#0F1620",
                                border: "1px solid #1B2A3A",
                                fontFamily: "JetBrains Mono",
                                fontSize: 11,
                                color: "#E6EDF6",
                            }}
                            labelFormatter={(ts) => fmtTime(ts)}
                            formatter={(value, name) =>
                                name === "volume"
                                    ? [fmtVol(value), "Volume"]
                                    : [`$${Number(value).toFixed(decimals)}`, symbol]
                            }
                        />
                        {/* Volume bars first so they render behind the price line */}
                        <Bar
                            yAxisId="vol"
                            dataKey="volume"
                            fill="rgba(61,167,255,0.22)"
                            isAnimationActive={false}
                            barSize={3}
                        />
                        <Line
                            yAxisId="price"
                            type="monotone"
                            dataKey="price"
                            stroke="#3DA7FF"
                            strokeWidth={1.6}
                            dot={false}
                            isAnimationActive={false}
                        />
                        {buys.slice(0, 10).map((b) => (
                            <ReferenceLine
                                yAxisId="price"
                                key={`b-${b.grid_id}`}
                                y={b.price}
                                stroke="rgba(43,227,139,0.35)"
                                strokeDasharray="3 3"
                                strokeWidth={1}
                            />
                        ))}
                        {sells.slice(0, 30).map((s) => (
                            <ReferenceLine
                                yAxisId="price"
                                key={`s-${s.grid_id}`}
                                y={s.price}
                                stroke="rgba(255,92,108,0.35)"
                                strokeDasharray="3 3"
                                strokeWidth={1}
                            />
                        ))}
                    </ComposedChart>
                </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

export default PriceChart;
